import React from 'react';
import { Timer, Hourglass } from 'lucide-react';

// Bounds mirror app/agent/call_limits.py (the agent clamps to these as well)
const SILENCE_MIN_S = 5;
const SILENCE_MAX_S = 600;
const MAX_DURATION_MIN_MIN = 1;
const MAX_DURATION_MAX_MIN = 240;

const clamp = (value, lo, hi, fallback) => {
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) return fallback;
    return Math.max(lo, Math.min(hi, n));
};

const Toggle = ({ enabled, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
            enabled ? 'bg-primary' : 'bg-muted'
        }`}
    >
        <span
            className={`inline-block h-4 w-4 flex-shrink-0 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
        />
    </button>
);

const CallLimitsTab = ({ formData, setFormData }) => {
    const silenceEnabled = formData.silence_hangup_enabled ?? false;
    const silenceSeconds = formData.silence_hangup_seconds ?? 30;
    const maxEnabled = formData.max_call_duration_enabled ?? false;
    const maxSeconds = formData.max_call_duration_seconds ?? 600;
    const maxMinutes = maxSeconds === '' ? '' : Math.round(maxSeconds / 60);

    const update = (changes) => setFormData({ ...formData, ...changes });
    const inputClass = 'w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary';

    return (
        <div className="flex-1 overflow-y-auto min-h-0 p-6"
            style={{
                scrollBehavior: 'smooth',
                maxHeight: 'calc(100vh - 280px)'
            }}
        >
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Hang Up After Silence */}
                <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                            <Timer className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-foreground">Hang Up After Silence</h3>
                            <p className="text-sm text-muted-foreground">End the call when the caller stops responding</p>
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                        <div className="min-w-0 pr-4">
                            <label className="text-sm font-medium text-foreground">Enable Hang Up After Silence</label>
                            <p className="text-xs text-muted-foreground mt-1">
                                The agent ends the call once the caller has said nothing for the time below.
                                Time the agent spends speaking or working is not counted, and reminder phrases do not restart the clock.
                            </p>
                        </div>
                        <Toggle enabled={silenceEnabled} onClick={() => update({ silence_hangup_enabled: !silenceEnabled })} />
                    </div>

                    {silenceEnabled && (
                        <div className="mt-4 space-y-4">
                            <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                                <label className="text-sm font-medium text-foreground">Silence Before Hanging Up (seconds)</label>
                                <input
                                    type="number"
                                    min={SILENCE_MIN_S}
                                    max={SILENCE_MAX_S}
                                    value={silenceSeconds}
                                    onChange={(e) => update({ silence_hangup_seconds: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
                                    onBlur={() => update({ silence_hangup_seconds: clamp(silenceSeconds, SILENCE_MIN_S, SILENCE_MAX_S, 30) })}
                                    className={`${inputClass} mt-2`}
                                />
                                <p className="text-xs text-muted-foreground mt-2">
                                    Between {SILENCE_MIN_S} and {SILENCE_MAX_S} seconds. If reminders are on, set this longer than the reminder timer so the caller hears a reminder first.
                                </p>
                            </div>
                            <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                                <label className="text-sm font-medium text-foreground">Closing Message (optional)</label>
                                <textarea
                                    value={formData.silence_hangup_message || ''}
                                    onChange={(e) => update({ silence_hangup_message: e.target.value })}
                                    rows={2}
                                    placeholder="Since I haven't heard from you, I'll end the call now. Goodbye."
                                    className={`${inputClass} mt-2 resize-none`}
                                />
                                <p className="text-xs text-muted-foreground mt-2">The agent says this word for word before hanging up. Leave it blank to hang up without a message.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Max Call Duration */}
                <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                            <Hourglass className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-foreground">Max Call Duration</h3>
                            <p className="text-sm text-muted-foreground">End the call once it reaches a set length</p>
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                        <div className="min-w-0 pr-4">
                            <label className="text-sm font-medium text-foreground">Enable Max Call Duration</label>
                            <p className="text-xs text-muted-foreground mt-1">
                                The agent ends the call when it has lasted this long. If a call transfer is in progress at that moment, the agent waits for the transfer to finish.
                            </p>
                        </div>
                        <Toggle enabled={maxEnabled} onClick={() => update({ max_call_duration_enabled: !maxEnabled })} />
                    </div>

                    {maxEnabled && (
                        <div className="mt-4 space-y-4">
                            <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                                <label className="text-sm font-medium text-foreground">Max Call Duration (minutes)</label>
                                <input
                                    type="number"
                                    min={MAX_DURATION_MIN_MIN}
                                    max={MAX_DURATION_MAX_MIN}
                                    value={maxMinutes}
                                    onChange={(e) => update({ max_call_duration_seconds: e.target.value === '' ? '' : parseInt(e.target.value, 10) * 60 })}
                                    onBlur={() => update({ max_call_duration_seconds: clamp(maxMinutes, MAX_DURATION_MIN_MIN, MAX_DURATION_MAX_MIN, 10) * 60 })}
                                    className={`${inputClass} mt-2`}
                                />
                                <p className="text-xs text-muted-foreground mt-2">
                                    Between {MAX_DURATION_MIN_MIN} and {MAX_DURATION_MAX_MIN} minutes, counted from when the agent joins the call. On outbound calls that includes ringing time.
                                </p>
                            </div>
                            <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                                <label className="text-sm font-medium text-foreground">Closing Message (optional)</label>
                                <textarea
                                    value={formData.max_call_duration_message || ''}
                                    onChange={(e) => update({ max_call_duration_message: e.target.value })}
                                    rows={2}
                                    placeholder="We've reached the time limit for this call. Thank you for calling, goodbye."
                                    className={`${inputClass} mt-2 resize-none`}
                                />
                                <p className="text-xs text-muted-foreground mt-2">The agent says this word for word before hanging up. Leave it blank to hang up without a message.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CallLimitsTab;
